import { useState } from "react";
import { Receipt, Table, CheckCircle, Warning, SpinnerGap } from "@phosphor-icons/react";

const FEE_HEAD = { govt_fees: "Govt", private_fees: "Private", regular_fees: "Regular", other_fees: "Mgmt/Other", nri_fees: "NRI" };
const APPLY_COLS = ["govt_fees", "private_fees", "other_fees", "nri_fees"];
const OPTIONAL_LABELS = { seat_intake: "Seats", established_year: "Est. year", college_status: "Status" };

export default function CsvReconCard({ card, feesMode, onOpen, onApply }) {
  const present = card.present_columns || APPLY_COLS.concat(["seat_intake"]);
  const [cols, setCols] = useState(() => new Set(APPLY_COLS.filter((c) => present.includes(c))));
  const [extra, setExtra] = useState(() => new Set(["seat_intake", "established_year", "college_status"].filter((f) => present.includes(f))));   // present college-level fields default ON
  const [createNew, setCreateNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (card.error) {
    return (
      <div className="flex gap-3 animate-fade-in" data-testid="csv-recon-error">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-rose-400 flex items-center justify-center shrink-0">
          <Warning size={18} weight="fill" className="text-white" />
        </div>
        <div className="bg-rose-50 border border-rose-200 px-4 py-3 rounded-3xl rounded-tl-md text-sm text-rose-700 max-w-[85%]">
          <b>{card.filename}</b> — {card.error}
        </div>
      </div>
    );
  }

  const bs = card.by_status || {};
  const matched = (card.total || 0) - (card.unmatched || 0);
  const toggle = (c) => {
    if (!present.includes(c)) return;   // only CSV-present columns are selectable
    const next = new Set(cols);
    next.has(c) ? next.delete(c) : next.add(c);
    setCols(next);
  };
  const toggleExtra = (f) => {
    if (!present.includes(f)) return;
    const next = new Set(extra);
    next.has(f) ? next.delete(f) : next.add(f);
    setExtra(next);
  };
  const applyColumns = [...Array.from(cols), ...Array.from(extra)];
  const nothingSelected = applyColumns.length === 0;
  const doApply = async () => {
    setBusy(true); setErr("");
    try {
      const res = await onApply(card.ingest_id, applyColumns, createNew);
      if (res?.error) setErr(res.error);
    } catch (e) { setErr(e?.response?.data?.detail || "Apply failed"); }
    setBusy(false);
  };

  return (
    <div className="flex gap-3 animate-fade-in" data-testid={`csv-recon-card-${card.ingest_id}`}>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#059669] to-[#10b981] flex items-center justify-center shrink-0">
        <Receipt size={18} weight="fill" className="text-white" />
      </div>
      <div className="max-w-[92%] w-full border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-4 rounded-3xl rounded-tl-md shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[13px] font-black text-[#09090B] truncate">{card.filename || "CSV"}</span>
          <span className="text-[10px] font-mono text-zinc-400">O(1) resolved · no LLM</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <Stat label="Total" value={card.total} tone="zinc" />
          <Stat label="Matched" value={matched} tone="emerald" />
          <Stat label="Unmatched" value={card.unmatched} tone={card.unmatched ? "rose" : "emerald"} />
          <Stat label="Data Update" value={bs.changed || 0} tone="amber" />
          <Stat label="Data New" value={bs.new || 0} tone="blue" />
          <Stat label="Unchanged" value={bs.unchanged || 0} tone="emerald" />
        </div>

        {card.by_method && Object.keys(card.by_method).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="csv-recon-methods">
            <span className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-400">Method</span>
            {Object.entries(card.by_method).map(([m, n]) => (
              <span key={m} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${m === "fuzzy" ? "bg-amber-100 text-amber-700" : m === "unmatched" ? "bg-rose-100 text-rose-700" : "bg-zinc-100 text-zinc-500"}`}>{m} · {n}</span>
            ))}
          </div>
        )}

        <button onClick={onOpen} data-testid={`csv-open-recon-${card.ingest_id}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#6043EF] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] transition-colors">
          <Table size={14} weight="bold" /> Open recon table
        </button>

        {card.applied ? (
          <div className="mt-3 flex items-center gap-2 text-[12px] font-bold text-emerald-700" data-testid="csv-applied-note">
            <CheckCircle size={16} weight="fill" /> Applied to database
            {card.applyResult?.fees_report?.counts && (
              <span className="font-normal text-zinc-500">
                · {card.applyResult.fees_report.counts.matched ?? matched} written
              </span>
            )}
          </div>
        ) : (
          <div className="mt-3 pt-3 border-t border-emerald-100">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2.5">
              <span className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-500">Apply columns</span>
              {APPLY_COLS.map((c) => {
                const avail = present.includes(c);
                return (
                  <label key={c} className={`inline-flex items-center gap-1.5 select-none ${avail ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
                    data-testid={`csv-col-${c}`} title={avail ? "" : "not in this CSV"}>
                    <input type="checkbox" checked={cols.has(c)} disabled={!avail} onChange={() => toggle(c)} className="w-3.5 h-3.5 accent-[#059669] cursor-pointer disabled:cursor-not-allowed" />
                    <span className={`text-[11px] font-semibold ${cols.has(c) ? "text-[#09090B]" : "text-zinc-400 line-through"}`}>{FEE_HEAD[c]}</span>
                  </label>
                );
              })}
              <span className="border-l border-emerald-200 h-4 self-center" />
              {["seat_intake", "established_year", "college_status"].map((f) => {
                const avail = present.includes(f);
                return (
                  <label key={f} className={`inline-flex items-center gap-1.5 select-none ${avail ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
                    data-testid={`csv-col-${f}`}
                    title={avail ? (f === "seat_intake" ? "Update top-level intake (total_seats)" : `Update ${OPTIONAL_LABELS[f]}`) : "not in this CSV"}>
                    <input type="checkbox" checked={extra.has(f)} disabled={!avail} onChange={() => toggleExtra(f)} className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer disabled:cursor-not-allowed" />
                    <span className={`text-[11px] font-semibold ${extra.has(f) ? "text-indigo-700" : "text-zinc-400 line-through"}`}>{OPTIONAL_LABELS[f] || f}</span>
                  </label>
                );
              })}
              {(card.unmatched || 0) > 0 && (
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none ml-1" data-testid="csv-create-new">
                  <input type="checkbox" checked={createNew} onChange={(e) => setCreateNew(e.target.checked)} className="w-3.5 h-3.5 accent-rose-500 cursor-pointer" />
                  <span className="text-[11px] font-semibold text-zinc-600">Create {card.unmatched} unmatched as new</span>
                </label>
              )}
            </div>
            <button onClick={doApply} disabled={busy || nothingSelected} data-testid={`csv-apply-${card.ingest_id}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#09090B] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-black disabled:opacity-50 transition-colors">
              {busy ? <SpinnerGap size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
              {busy ? "Applying…" : "Apply to database"}
            </button>
            {err && <span className="ml-3 text-[11px] font-bold text-rose-600">{err}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${tones[tone] || tones.zinc}`}>
      <span className="uppercase tracking-wider opacity-70">{label}</span>
      <span>{value ?? 0}</span>
    </span>
  );
}
